import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

interface HealthResponse {
  status: string;
  service: string;
  app_version?: string;
  build?: {
    git_sha: string;
    git_full_sha: string;
    build_time: string;
  };
}

/**
 * Renders `vX.Y.Z · <short-sha>` (or `· dev build` for non-Docker
 * runs) sourced from `/api/health`. Plain text — the upstream Forgejo
 * is private, so a "click to view commit" link would just frustrate
 * the reader.
 */
export function VersionBadge() {
  const health = useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/api/health'),
    staleTime: 5 * 60_000,
  });

  if (!health.data?.build) return null;
  const { git_sha, build_time } = health.data.build;
  const appVersion = health.data.app_version ?? '0.1.x';
  const isDev = git_sha === 'dev' || git_sha === 'unknown';
  const built = formatBuiltAt(build_time);

  const label = isDev
    ? `v${appVersion} · dev build`
    : `v${appVersion} · ${git_sha}`;

  return (
    <span
      className="font-mono text-xs text-stone-400 dark:text-stone-500"
      title={`Built ${built}`}
    >
      {label}
    </span>
  );
}

function formatBuiltAt(iso: string): string {
  if (!iso || iso === 'unknown') return 'unknown';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
